/**
 * Shared submit handler for the lead-gen forms (request for proposal, tradeshow, become a dealer,
 * business services, industries audit, industry hero, ...). They all POST to the same
 * B2CWebsitePublic create-lead-gen-form-item endpoint, so the FormData -> typed-request mapping lives
 * here once. Each form assigns it: `formHandler.submitHandler = submitLeadGenForm;`.
 *
 * The client sends snake_case field names that match the form inputs; a few older forms use variant
 * names (phone_number, company, city, ...), handled here via fallbacks.
 */
//Declared with var + function (not const) so pages that include this file once per lead-gen form
//can load it more than once without a redeclaration error.
var leadGenFormB2CApi = getDivZeroWebPublicApi(DivZeroWebPublicApi.B2CWebsitePublicApi, false);

//Field names that map to a dedicated request property (or are non-data plumbing). Anything else a
//form submits is form-specific and gets folded into `notes` so no data is lost.
var LEAD_GEN_HANDLED_FIELDS = new Set([
    "g-recaptcha-response", "website_id", "tos_check",
    "source_url", "source_name", "lead_type", "industry",
    "first_name", "last_name", "job_title", "email",
    "phone_nb", "phone_number", "company_name", "company",
    "royer_site_customer", "how_can_we_help", "order_nb", "return_request_nb",
    "nb_of_employees_req_boots", "employee_count",
    "collections_interest", "products_interest", "notes",
    "address_street", "address", "address_local", "address_city", "city",
    "address_postal_code", "postal_code", "address_region_code", "region_code",
    "address_country_code", "tradeshow_name",
]);

/**
 * @param {FormData} formData
 * @returns {Promise<boolean>}
 */
async function submitLeadGenForm(formData) {
    //First non-empty value among the given field names (null if none present).
    /**
     * @param {...string} names
     * @returns {string | null}
     */
    const text = (...names) => {
        for (const name of names) {
            const value = formData.get(name);
            if (typeof value === "string" && value !== "") return value;
        }
        return null;
    };
    /** @param {string} name @returns {number | null} */
    const intOrNull = (name) => {
        const value = parseInt(text(name) ?? "");
        return Number.isNaN(value) ? null : value;
    };
    /** @param {string} name @returns {string[]} */
    const list = (name) =>
        /** @type {string[]} */ (formData.getAll(name).filter(value => typeof value === "string" && value !== ""));

    const siteCustomerRaw = text("royer_site_customer");

    //Fold any form-specific fields (no dedicated request property) into notes, as labeled lines.
    const extraNotes = [];
    for (const [name, value] of formData.entries()) {
        if (LEAD_GEN_HANDLED_FIELDS.has(name)) continue;
        if (typeof value !== "string" || value === "") continue;
        const label = name.replace(/_/g, " ").replace(/\b\w/g, character => character.toUpperCase());
        extraNotes.push(`${label}: ${value}`);
    }
    const notes = [text("notes"), ...extraNotes].filter(Boolean).join("\n") || null;

    const created = await leadGenFormB2CApi.b2CWebsitePublicLeadGenForm({
        websiteId: siteId,
        countryCode: countryCode,
        lang: locale,
        gRecaptchaResponse: text("g-recaptcha-response"),
        sourceUrl: text("source_url"),
        sourceName: text("source_name"),
        leadType: text("lead_type"),
        industry: text("industry"),
        firstName: text("first_name"),
        lastName: text("last_name"),
        jobTitle: text("job_title"),
        email: text("email"),
        phoneNb: text("phone_nb", "phone_number"),
        companyName: text("company_name", "company"),
        royerSiteCustomer: siteCustomerRaw == null ? null : siteCustomerRaw === "true",
        howCanWeHelp: text("how_can_we_help"),
        orderNb: intOrNull("order_nb"),
        returnRequestNb: intOrNull("return_request_nb"),
        nbOfEmployeesReqBoots: text("nb_of_employees_req_boots", "employee_count"),
        collectionsInterest: list("collections_interest"),
        productsInterest: list("products_interest"),
        notes: notes,
        addressStreet: text("address_street", "address"),
        addressLocal: text("address_local"),
        addressCity: text("address_city", "city"),
        addressPostalCode: text("address_postal_code", "postal_code"),
        addressRegionCode: text("address_region_code", "region_code"),
        addressCountryCode: text("address_country_code"),
        tradeshowName: text("tradeshow_name"),
    });

    if (!created) throw new Error("Lead gen form was not submitted");
    return created;
}
